<template>
  <div class="flex items-center gap-2">
    <span v-if="label" class="text-sm font-bold">{{ label }}</span>
    <div ref="container" class="relative" @focusout="handleFocusOut">
      <input
        type="time"
        :value="timeValue"
        :step="isWideLayout ? DESKTOP_STEP_SECONDS : MOBILE_STEP_SECONDS"
        :disabled="disabled"
        :class="
          input({ size: 'sm', class: 'time-input w-[5.5rem] pl-2 pr-[1.75rem] tabular-nums' })
        "
        :aria-label="label ?? t('time')"
        :data-testid="`datepicker-time${testIdSuffix ?? ''}`"
        @input="handleInput"
        @focus="openList"
        @click="openList"
      />
      <button
        v-if="isWideLayout"
        type="button"
        class="absolute right-2 top-1/2 -translate-y-1/2 text-hazel-500 hover:text-hazel-700 disabled:opacity-50"
        :disabled="disabled"
        :aria-label="t('chooseTime')"
        :aria-expanded="isListOpen"
        :data-testid="`datepicker-time-toggle${testIdSuffix ?? ''}`"
        @click="toggleList"
      >
        <ClockIcon class="size-4" />
      </button>
      <ClockIcon
        v-else
        class="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-hazel-500"
      />

      <div
        v-if="isListOpen"
        ref="timeList"
        :style="timeListStyles"
        :class="float({ class: 'z-30 flex gap-1 p-1' })"
        :data-testid="`datepicker-time-list${testIdSuffix ?? ''}`"
      >
        <div ref="hourList" class="time-column">
          <button
            v-for="hour in HOURS"
            :key="hour"
            type="button"
            class="time-option"
            :class="{ selected: hour === modelValue.getHours() }"
            :data-testid="`datepicker-time-hour-${hour}`"
            @click="setTime(hour, modelValue.getMinutes())"
          >
            {{ padTwo(hour) }}
          </button>
        </div>
        <div ref="minuteList" class="time-column">
          <button
            v-for="minute in MINUTES"
            :key="minute"
            type="button"
            class="time-option"
            :class="{ selected: minute === modelValue.getMinutes() }"
            :data-testid="`datepicker-time-minute-${minute}`"
            @click="handleMinuteSelect(minute)"
          >
            {{ padTwo(minute) }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside, useBreakpoints } from '@vueuse/core';
import { ClockIcon } from 'lucide-vue-next';
import { input } from '@carvia/theme/lib/cva/input';
import { float } from '@carvia/theme/lib/cva/float';
import { cvBreakpoints } from '@/utils/breakpoints';
import { useDatepickerFloating } from './use-datepicker-floating';

const DESKTOP_STEP_SECONDS = 5 * 60;
const MOBILE_STEP_SECONDS = 60;
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 / 5 }, (_, i) => i * 5);

const props = defineProps<{
  modelValue: Date;
  label?: string;
  disabled?: boolean;
  testIdSuffix?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date): void;
}>();

const { t } = useI18n();
// Desktop gets the ROS-styled list in 5 minute steps; mobile keeps the native OS time picker.
const isWideLayout = useBreakpoints(cvBreakpoints).greaterOrEqual('md');

const padTwo = (value: number) => String(value).padStart(2, '0');

const timeValue = computed(
  () => `${padTwo(props.modelValue.getHours())}:${padTwo(props.modelValue.getMinutes())}`,
);

const setTime = (hours: number, minutes: number) => {
  const date = new Date(props.modelValue);
  date.setHours(hours, minutes, 0, 0);
  emit('update:modelValue', date);
};

const handleInput = (event: Event) => {
  const [hours, minutes] = (event.target as HTMLInputElement).value.split(':').map(Number);
  // Empty while the user clears a segment; keep the last complete time.
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return;
  setTime(hours, minutes);
};

const isListOpen = ref(false);
const container = useTemplateRef<HTMLElement>('container');
const hourList = useTemplateRef<HTMLElement>('hourList');
const minuteList = useTemplateRef<HTMLElement>('minuteList');
const timeList = useTemplateRef<HTMLElement>('timeList');
const timeListStyles = useDatepickerFloating(container, timeList);

const scrollToSelected = async ({ onlyIfHidden = false } = {}) => {
  await nextTick();
  // Scroll only the columns (scrollIntoView would also move the page and picker).
  for (const list of [hourList.value, minuteList.value]) {
    const selected = list?.querySelector<HTMLElement>('.selected');
    if (!list || !selected) continue;
    const isVisible =
      selected.offsetTop >= list.scrollTop &&
      selected.offsetTop + selected.offsetHeight <= list.scrollTop + list.clientHeight;
    if (onlyIfHidden && isVisible) continue;
    list.scrollTop = selected.offsetTop - (list.clientHeight - selected.clientHeight) / 2;
  }
};

const openList = () => {
  if (!isWideLayout.value || props.disabled || isListOpen.value) return;
  isListOpen.value = true;
  scrollToSelected();
};

const closeList = () => {
  isListOpen.value = false;
};

const toggleList = () => {
  if (isListOpen.value) closeList();
  else openList();
};

const handleMinuteSelect = (minute: number) => {
  setTime(props.modelValue.getHours(), minute);
  closeList();
};

const handleFocusOut = (event: FocusEvent) => {
  // Null when focus lands on nothing focusable, e.g. a list button in Safari; a click outside closes then.
  const nextTarget = event.relatedTarget;
  if (nextTarget instanceof Node && !container.value?.contains(nextTarget)) closeList();
};

// Keep a typed time in view; a time picked in the list is already visible and must not jump.
watch(timeValue, () => {
  if (isListOpen.value) scrollToSelected({ onlyIfHidden: true });
});

onClickOutside(container, closeList);
</script>

<style scoped lang="scss">
/* The ROS clock icon and list replace the native, unstylable picker popup. */
.time-input::-webkit-calendar-picker-indicator {
  display: none;
}

/* Exactly seven options (7 × 2.15rem + 6 × 0.125rem gap), so the selected one sits centered. */
.time-column {
  @apply relative flex max-h-[15.8rem] w-[3.5rem] flex-col gap-0.5 overflow-y-auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.time-option {
  @apply shrink-0 rounded-md py-1.5 text-center text-sm tabular-nums hover:bg-obsidian-100;

  &.selected {
    @apply bg-obsidian-600 text-white;
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "time": "Time",
    "chooseTime": "Choose time"
  },
  "de": {
    "time": "Uhrzeit",
    "chooseTime": "Uhrzeit auswählen"
  },
  "es": {
    "time": "Hora",
    "chooseTime": "Elegir hora"
  }
}
</i18n>
